import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const UpcomingTrips = ({ trips }) => {
  const navigate = useNavigate();
  const [countdowns, setCountdowns] = useState({});

  useEffect(() => {
    const updateCountdowns = () => {
      const newCountdowns = {};
      trips?.forEach(trip => {
        const now = new Date();
        const tripDate = new Date(trip.startDate);
        const timeDiff = tripDate - now;
        
        if (timeDiff > 0) {
          const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          newCountdowns[trip.id] = { days, hours };
        } else {
          newCountdowns[trip.id] = { days: 0, hours: 0 };
        }
      });
      setCountdowns(newCountdowns);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 3600000); // Update every hour

    return () => clearInterval(interval);
  }, [trips]);

  const handleViewDetails = (tripId) => {
    navigate(`/booking-flow?booking=${tripId}`);
  };

  const handleViewTour = (tourId) => {
    navigate(`/tour-detail?id=${tourId}`);
  };

  if (trips?.length === 0) {
    return (
      <div className="bg-card rounded-xl p-8 border border-border text-center">
        <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Upcoming Trips</h3>
        <p className="text-muted-foreground mb-4">Start planning your next adventure!</p>
        <Button onClick={() => navigate('/homepage')} iconName="Plus" iconPosition="left">
          Browse Tours
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {trips?.map((trip) => (
        <div key={trip?.id} className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
          <div className="flex flex-col lg:flex-row">
            {/* Trip Image */}
            <div className="lg:w-64 h-48 lg:h-auto relative overflow-hidden">
              <Image
                src={trip?.image}
                alt={trip?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  trip?.status === 'confirmed' ?'bg-success text-white' 
                    : trip?.status === 'pending' ?'bg-warning text-white' :'bg-muted text-muted-foreground'
                }`}>
                  {trip?.status?.charAt(0)?.toUpperCase() + trip?.status?.slice(1)}
                </span>
              </div>
            </div>

            {/* Trip Details */}
            <div className="flex-1 p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                <div className="flex-1 mb-4 lg:mb-0">
                  <h3 className="text-xl font-semibold text-foreground mb-2">{trip?.title}</h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-muted-foreground">
                      <Icon name="MapPin" size={16} className="mr-2" />
                      <span className="text-sm">{trip?.destination}</span>
                    </div>
                    <div className="flex items-center text-muted-foreground">
                      <Icon name="Calendar" size={16} className="mr-2" />
                      <span className="text-sm">
                        {new Date(trip.startDate)?.toLocaleDateString()} - {new Date(trip.endDate)?.toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center text-muted-foreground">
                      <Icon name="Users" size={16} className="mr-2" />
                      <span className="text-sm">{trip?.travelers} travelers</span>
                    </div>
                  </div>

                  {/* Countdown Timer */}
                  {countdowns?.[trip?.id] && countdowns?.[trip?.id]?.days > 0 && (
                    <div className="bg-primary/5 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-primary">Trip starts in:</span>
                        <div className="flex items-center space-x-4">
                          <div className="text-center">
                            <div className="text-lg font-bold text-primary">{countdowns?.[trip?.id]?.days}</div>
                            <div className="text-xs text-muted-foreground">days</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-primary">{countdowns?.[trip?.id]?.hours}</div>
                            <div className="text-xs text-muted-foreground">hours</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-2 lg:ml-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(trip?.id)}
                    iconName="Eye"
                    iconPosition="left"
                  >
                    View Details
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewTour(trip?.tourId)}
                    iconName="ExternalLink"
                    iconPosition="left"
                  >
                    View Tour
                  </Button>
                  {trip?.status === 'confirmed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                    >
                      Download Ticket
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UpcomingTrips;